/**
 * The route a signed-out visitor was going to, remembered once and resumed after signing
 * in (docs/ux/PLATFORM-ADMIN.md §18.2) — **its query string with it** (TAS-22's carried item, from
 * `fix-console-list-signin-dates`): a bookmarked Tenants link such as
 * `/tenants?q=acme&state=any&sort=createdAt&page=2` comes back as that list, not as `/tenants`.
 *
 * It arrives in the URL, so it is attacker-controllable: a link to
 * `/login?next=https://elsewhere.example` that the sign-in page obeys is an open redirect
 * wearing a session, and this application's session is the whole estate. Only a path
 * **inside this application** is ever followed.
 *
 * Three forms have to be refused rather than one. An absolute URL is the obvious case; a
 * protocol-relative `//elsewhere.example` is the one a leading-slash check waves through,
 * and browsers treat it as an absolute URL to another host. The third is the **backslash**
 * — per the WHATWG URL parser a `\` is a path separator for special schemes, so
 * `/\elsewhere.example` and `/\/elsewhere.example` both normalise to
 * `https://elsewhere.example/`. It is the standard bypass for exactly this guard, and it
 * reads as an in-application path to anything that only looks at the first character.
 *
 * **The query string is admitted, and nothing else is loosened.** The path part is exactly the
 * pattern it always was, and the query may hold anything that is not whitespace, a `#` or a
 * backslash: once the first `/` is followed by a non-`/`, non-`\` character the browser has already
 * decided the host is this one, and what comes after a `?` is data for this application's own
 * parser, never a place to go. Whitespace stays out because a browser strips tab and newline from
 * inside a URL (`/\t/elsewhere.example` would become `//elsewhere.example`), and a fragment stays
 * out because nothing here uses one.
 */
const IN_APPLICATION_PATH = /^\/(?!\/)[^\s?#\\]*(?:\?[^\s#\\]*)?$/

/**
 * `/login` is never resumed. It *is* a route in this application, so the check above
 * admits it, and following it would land a just-signed-in owner back on the sign-in form
 * — which renders perfectly well while signed in, because it hangs off the root route
 * rather than off the authenticated one. Refused here rather than fixed there: "signing
 * in takes you somewhere useful" is a property of the route being resumed. (`/login?next=…` is the
 * same page, so the query does not buy a way past it.)
 *
 * **It is tested on the decoded path as well as on the raw string** (TAS-22 review round 2, N2).
 * The router matches a path after percent-decoding it, so `/%6Cogin` — and `/%4Cogin`, and any
 * percent-encoded letter of `login`, in any mix — *is* the sign-in page even though the raw string
 * does not read `/login`. See {@link decodedForms}.
 */
const SIGN_IN_PATH = /^\/login(?:[/?]|$)/i

/**
 * The string itself, then what it reads as after each round of percent-decoding, until nothing
 * changes (at most {@link MAX_DECODE_ROUNDS}). Every valid `%XX` is decoded on its own, so one
 * malformed escape elsewhere in the string cannot stop the rest being read — a router that decodes
 * leniently would read `/%6Cogin/%` as `/login/%`, and a decoder that throws on the `%` would have
 * hidden that.
 *
 * Several rounds, not one, because refusing a lookalike costs nothing (no address this application
 * makes is percent-encoded twice) and trusting that the router decodes exactly once is a bet on
 * code that is not ours.
 */
const MAX_DECODE_ROUNDS = 4

function decodedForms(value: string): string[] {
  const forms = [value]
  let current = value
  for (let round = 0; round < MAX_DECODE_ROUNDS; round++) {
    const next = current.replace(/%([0-9a-f]{2})/gi, (_match, hex: string) =>
      String.fromCharCode(Number.parseInt(hex, 16)),
    )
    if (next === current) {
      break
    }
    forms.push(next)
    current = next
  }
  return forms
}

/**
 * Two ways past the `/login` check above that need their own refusal (TAS-22 review, N2):
 *
 * - **A control character anywhere** (`/login%00`, once decoded, is `/login` plus a NUL). A browser
 *   and the router's own path cleaning strip a trailing one, so the string that looked like another
 *   page resolved to the sign-in form. No address this application makes holds one.
 * - **A dot segment** (`/tenants/../login`, `/%2e%2e/login`): the URL parser collapses it, so the
 *   address that was checked is not the address that is followed. No address this application makes
 *   holds one either, so the segment is refused rather than resolved.
 */
const CONTROL_CHARACTER = /\p{Cc}/u
const DOT_SEGMENT = /(?:^|\/)(?:\.|%2e){1,2}(?=\/|$)/i

export function safeNextPath(value: unknown): string | undefined {
  if (
    typeof value !== 'string' ||
    !IN_APPLICATION_PATH.test(value) ||
    decodedForms(value).some((form) => SIGN_IN_PATH.test(form)) ||
    CONTROL_CHARACTER.test(value) ||
    DOT_SEGMENT.test(value.split('?', 1)[0] ?? '')
  ) {
    return undefined
  }
  return value
}

/**
 * What to remember about the location a signed-out visitor was going to: its path and its query
 * string, as the router holds them (`searchStr` keeps the leading `?`, and is empty for none).
 * The fragment is not remembered.
 *
 * If the whole of it does not pass {@link safeNextPath} — a query the guard refuses — the path
 * alone is remembered, so a strange query costs the owner their parameters and never the page.
 */
export function nextFromLocation(location: {
  readonly pathname: string
  readonly searchStr: string
}): string | undefined {
  return (
    safeNextPath(`${location.pathname}${location.searchStr}`) ?? safeNextPath(location.pathname)
  )
}
